import React, {CSSProperties, useCallback, useEffect, useMemo, useState} from 'react';
import cn from 'classnames';
import {browserHistory} from 'react-router';
import {Map} from 'immutable';
import throttle from 'lodash/throttle';
import upperFirst from 'lodash/upperFirst';

import FieldContainer from 'components/pure/utils/form/FieldContainer';
import {
  AssignmentFrequency,
  exampleKpiValue,
  formatAssignmentPeriod,
  formatKpiPeriod,
  KpiAssignee,
  KpiAssignment,
  KpiAssignmentId,
  KpiAssignmentReminderOptions,
  KpiReminder,
  KpiTarget,
  KpiTargetTree,
  periodIsShorter,
} from '@store/@types/kpi';
import Button from '@material-ui/core/Button';
import SquareToggleButton from 'components/SquareToggleButton';
import TemplatedMessage from '@components/templated-message';
import {makeStyles} from '@components/@theme';
import {ModifyKpiAssignmentRequest} from '@store/kpi/KpiTargetSource';
import Time from 'records/Time';
import KpiGoalActions from '@store/kpi/KpiTargetActions';
import FrequencyField from '$pages/@common/frequency-field';
import AssignToField from './assign-to.field';
import {
  ALLOWED_FIELDS,
  DEFAULT_TEMPLATE,
} from '@components/templated-message/templated-message.consts';
import moment from 'moment';
import User, {UserId} from 'records/User';
import UserActions from 'actions/UserActions';
import TabPanel from 'components/coaching/widgets/TabPanel';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import {SlackIcon} from '@components/icon/SlackIcon';
import {GaugeHighIcon} from '@components/icon/GaugeHighIcon';
import {ChartLineUpIcon} from '@components/icon/ChartLineUpIcon';

interface AssignmentFormProps {
  getAssignment: (id: KpiAssignmentId) => KpiAssignment;
  getKpiById: (id: any) => KpiTargetTree;
  params: {assignmentId?: string; kpiId: string};
  users: Map<UserId, User>;

  fetchGoalKpiTree: typeof KpiGoalActions.fetchGoalKpiTree;
  allAssignments: typeof KpiGoalActions.allAssignments;
  fetchUsers: typeof UserActions.fetchAll;
}

interface FormState extends Partial<KpiReminder> {
  assignee?: KpiAssignee;
}
const defaultState = (assignment?: KpiAssignment): FormState => ({
  options: {requestTemplate: DEFAULT_TEMPLATE, initialTemplate: DEFAULT_TEMPLATE}, // TODO different defaults?
  frequency: AssignmentFrequency.DAILY,
  askViaDashboard: true,
  assignee: assignment?.assignee,
  ...(assignment?.reminder ?? {}),
  timeOfDay: new Time(assignment?.reminder?.timeOfDay ?? {hour: 17, minute: 0}),
});

export const AssignmentForm: React.FC<AssignmentFormProps> = ({
  getAssignment,
  fetchGoalKpiTree,
  allAssignments,
  getKpiById,
  params,
  users,
  fetchUsers,
}) => {
  const assignment = useMemo(() => {
    return getAssignment?.(parseInt(params.assignmentId || '', 10));
  }, [getAssignment, params.assignmentId]);

  useEffect(() => {
    fetchGoalKpiTree().then().catch(console.error);
    allAssignments().then().catch(console.error);
    fetchUsers({}).then().catch(console.error);
  }, [fetchGoalKpiTree, allAssignments, fetchUsers]);

  const [formState, setFormState] = useState(defaultState(assignment));
  const [messageTab, setMessageTab] = useState(0);

  useEffect(() => setFormState(defaultState(assignment)), [assignment]);

  const setState = useCallback(
    (data: FormState) => setFormState(formState => ({...formState, ...data})),
    [setFormState]
  );
  const toggle = useCallback(
    (field: keyof FormState) => {
      setFormState(formState => ({...formState, [field]: !formState[field]}));
    },
    [setFormState]
  );

  const kpiTarget = useMemo<KpiTarget | undefined>(() => {
    return getKpiById(params?.kpiId)?.kpiTarget;
  }, [getKpiById, params]);

  const handleChangeSlack = useCallback(
    val => setFormState(s => ({...s, options: {...s.options!, initialTemplate: val}})),
    [setFormState]
  );
  const handleChangePopup = useCallback(
    val => setFormState(s => ({...s, options: {...s.options!, requestTemplate: val}})),
    [setFormState]
  );

  const css = useStyles();

  if (!kpiTarget) {
    return null;
  }

  return (
    <div className={css.assignmentForm}>
      <h1 className={css.header}>Assign Goal "{kpiTarget.name}"</h1>
      <AssignToField
        kpiId={kpiTarget.id}
        fieldClassName={css.fieldContainer}
        labelClassName={css.fieldLabel}
        value={formState.assignee}
        onChange={assignee => setFormState(f => ({...f, assignee}))}
        assignmentId={assignment?.id}
      />
      <FieldContainer
        className={css.fieldContainer}
        labelClassName={css.fieldLabel}
        label="Ask for progress">
        <FrequencyField value={formState} onChange={setState} kpiTarget={kpiTarget} />
      </FieldContainer>
      <div className={css.askViaField}>
        <div className={css.askViaLabel}>Ask via</div>
        <div className={css.fieldContainer}>
          <div className={css.viaCard}>
            <div className={css.methodCheckboxes}>
              <SquareToggleButton
                textPosition="right"
                width={155}
                icon={SlackIcon}
                text="Slack"
                value={formState.askViaSlack}
                onToggle={() => toggle('askViaSlack')}
              />
              <SquareToggleButton
                textPosition="right"
                width={155}
                icon={GaugeHighIcon}
                text="Dashboard"
                value={formState.askViaDashboard}
                onToggle={() => toggle('askViaDashboard')}
              />
              <SquareToggleButton
                textPosition="right"
                width={155}
                icon={ChartLineUpIcon}
                text="Coaching"
                value={formState.askViaCoaching}
                onToggle={() => toggle('askViaCoaching')}
              />
            </div>
            <p
              className={cn(css.methodBanner, {
                [css.hide]: !formState.askViaDashboard,
              })}>
              Progress requests via Dashboard: <span className={css.on}>ON</span>
            </p>
            <p
              className={cn(css.methodBanner, {
                [css.hide]: !formState.askViaCoaching,
              })}>
              Progress requests via Coaching Meetings: <span className={css.on}>ON</span>
            </p>
            <div
              className={cn({
                [css.hide]: !formState.askViaSlack,
              })}>
              {messageTab === 0 ? (
                <TemplatedMessage
                  className={css.templateWrapper}
                  textClassname={css.textBorder}
                  title={
                    <div className={css.titleWrapper}>
                      <p>
                        Progress requests via Slack: <span className={css.on}>ON</span>
                      </p>
                      <p>Slack message template: </p>
                      <TextEquality options={formState.options!} />
                      <TabPanel
                        className={css.tabPanel}
                        onChange={setMessageTab}
                        tabs={['Slack Message', 'Popup Message']}
                        defaultSelectedIdx={0}
                        panels={[<div />, <div />]}
                      />
                    </div>
                  }
                  onChange={handleChangeSlack}
                  value={formState.options!.initialTemplate}
                  templateValues={getTemplate(
                    kpiTarget,
                    formState,
                    users.get(formState.assignee?.id)
                  )}
                />
              ) : (
                <TemplatedMessage
                  className={css.templateWrapper}
                  textClassname={css.textBorder}
                  title={
                    <div className={css.titleWrapper}>
                      <p>
                        Progress requests via Popup: <span className={css.on}>ON</span>
                      </p>
                      <p>Popup message template: </p>
                      <TextEquality options={formState.options!} />
                      <TabPanel
                        className={css.tabPanel}
                        onChange={setMessageTab}
                        panels={[<div />, <div />]}
                        tabs={['Slack Message', 'Popup Message']}
                        defaultSelectedIdx={1}
                      />
                    </div>
                  }
                  onChange={handleChangePopup}
                  value={formState.options!.requestTemplate}
                  templateValues={getTemplate(
                    kpiTarget,
                    formState,
                    users.get(formState.assignee?.id)
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={css.actionsStyle}>
        <Button
          variant="contained"
          color="primary"
          disabled={!validate(formState, kpiTarget)}
          onClick={() => submit(formState, kpiTarget, assignment?.id)}>
          <span>Assign Goal</span>
        </Button>
        <Button onClick={() => browserHistory.push(`/admin/kpis?tabIndex=1`)}>
          <span>Cancel</span>
        </Button>
      </div>
    </div>
  );
};

function TextEquality({
  options: {initialTemplate, requestTemplate},
}: {
  options: KpiAssignmentReminderOptions;
}) {
  const css = useStyles();

  const match = initialTemplate === requestTemplate;
  const tooltip = match ? 'Messages match' : 'Messages do not match';
  const notEqual = <span>&#8800;</span>;
  const equal = <span>=</span>;

  return (
    <GenericTooltip hoverBehavior positionTop tooltip={tooltip}>
      <div className={css.textMatch}>{match ? equal : notEqual}</div>
    </GenericTooltip>
  );
}

function getTemplate(
  kpi: KpiTarget,
  assignment: FormState,
  rep?: User
): Map<ALLOWED_FIELDS, string> {
  return Map<ALLOWED_FIELDS, string>({
    // kpi driven
    [ALLOWED_FIELDS.ACH_DESCRIPTION]: kpi.name,
    [ALLOWED_FIELDS.ACH_TARGET]: kpi.value,
    [ALLOWED_FIELDS.ACH_PERIOD]: formatKpiPeriod(kpi.frequency),
    [ALLOWED_FIELDS.ACH_FREQUENCY]: kpi.frequency.toLowerCase(),
    [ALLOWED_FIELDS.LAST_ACH_ENTERED]: exampleKpiValue(kpi.numericType),
    [ALLOWED_FIELDS.LAST_ACH_DATE]: moment().subtract(7, 'days').format('DD-MM-YYYY'),

    // assignment driven
    [ALLOWED_FIELDS.REMINDER_PERIOD]: formatAssignmentPeriod(assignment.frequency!),
    [ALLOWED_FIELDS.LAST_REMINDER_PERIOD]: upperFirst(
      formatAssignmentPeriod(assignment.frequency!, {tense: 'past'})
    ),
    [ALLOWED_FIELDS.REMINDER_FREQUENCY]: assignment.frequency?.toLowerCase(),
    [ALLOWED_FIELDS.FIRST_NAME]: rep?.firstName,
    [ALLOWED_FIELDS.LAST_NAME]: rep?.lastName,
  });
}

function validate(formState: FormState, kpi: KpiTarget): formState is Required<FormState> {
  if (isBlank(formState.options!.requestTemplate)) return false;
  if (isBlank(formState.options!.initialTemplate)) return false;
  if (!formState.askViaDashboard && !formState.askViaSlack && !formState.askViaCoaching) {
    return false;
  }
  if (!formState.assignee) return false;
  if (formState.frequency === AssignmentFrequency.WEEKLY && !formState.dayOfWeek) return false;
  if (formState.frequency === AssignmentFrequency.MONTHLY && !formState.dayOfMonth) return false;
  if (!periodIsShorter(formState.frequency, kpi.frequency)) return false;

  return true;
}

function isBlank(s: string | null | undefined): boolean {
  if (!s) {
    return true;
  }
  return !/\S/.test(s);
}

const submit = throttle(
  async (formState: FormState, kpi: KpiTarget, assignmentId?: KpiAssignmentId) => {
    if (!validate(formState, kpi)) {
      // TODO do something about it
    } else {
      const {assignee, ...reminder} = formState;
      const params: ModifyKpiAssignmentRequest = {
        assignee,
        kpiId: kpi.id,
        reminder,
      };

      try {
        if (assignmentId) {
          await KpiGoalActions.updateAssignment(assignmentId, params);
        } else {
          await KpiGoalActions.createAssignment(params);
        }
        await KpiGoalActions.allAssignments();
        browserHistory.push('/admin/kpis?tabIndex=1');
      } catch (e) {
        console.error(e);
      }
    }
  },
  2000
);

const TEMPLATE_OFFSET = 44;

const useStyles = makeStyles(theme => ({
  titleWrapper: {
    marginTop: -TEMPLATE_OFFSET + 1,
    position: 'relative',
    '& > p': {
      margin: '5px 0',
    },
  },
  textMatch: {
    position: 'absolute',
    left: 305,
    top: 58,
    width: 25,
    height: 25,
    backgroundColor: 'grey',
    borderRadius: 25,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 24,
    color: 'white',
  },
  tabPanel: {
    width: 300,
    height: 34,
    '& > .panel-switcher-row': {
      right: 'unset',
      left: 0,
      '& > .panel-tab > span': {
        padding: '0 15px',
      },
    },
  },
  textBorder: {
    borderTopLeftRadius: 0,
  },
  templateWrapper: {
    paddingTop: TEMPLATE_OFFSET + theme.spacing(2),
  },
  actionsStyle: {
    '& > button': {
      marginRight: 10,
    },
  },
  assignmentForm: {
    maxWidth: '900px',
  },
  fieldContainer: {
    '& > label.field-label': {
      justifyContent: 'start',
      '& $fieldLabel': {
        alignSelf: 'start',
        marginTop: '5px',
      },
    },
  },
  askViaField: {
    display: 'flex',
    flexFlow: 'row nowrap',
    margin: '20px 0',
  },
  askViaLabel: {
    width: '125px',
    margin: '5px 10px 0 0',
  },
  fieldLabel: {
    width: '125px',
    marginRight: 5,
  },
  input: {
    background: '#FFFFFF',
    border: '1px solid #BBBBBB',
    borderRadius: '3px',
  },
  methodCheckboxes: {
    marginBottom: '15px',
    display: 'flex',
    '& > .square-radio-button': {
      backgroundColor: '#FFFFFF',
      marginRight: '10px',
    } as CSSProperties,
  },
  viaCard: {
    width: '732px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F4F6F8',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #D7DDE5',
    padding: '15px',
    transition: 'height 500ms',
  },
  methodBanner: {
    background: '#FFFFFF',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
    borderRadius: theme.shape.borderRadius,
    padding: '20px 15px',
    margin: '0 0 15px 0',
    transition: 'opacity 500ms',
    '&:last-child': {
      marginBottom: '0',
    },
  },
  on: {
    color: '#E65F10',
    fontWeight: 'bold',
  },
  hide: {
    opacity: 0,
    display: 'none',
  },
  header: {
    marginBottom: 20,
  },
}));
